/**
 * Delivery receipts of outbound messages, shared by the API and the web. WhatsApp reports
 * sent, delivered, read, played (first play of a voice note) and failed; "queued" is ours
 * (accepted by DoneTalk, not yet by Meta). The status values live in vocabulary.ts.
 */

/**
 * Failures no new attempt can fix: the contact's choice, the content itself, or a template or
 * file that is gone. The message is not sent again, by hand or by itself.
 */
export const FINAL_SEND_ERRORS: ReadonlySet<string> = new Set([
  "131050", // the contact stopped marketing messages
  "131051", // message type not supported
  "368", // blocked for policy violations
  "132001", // template does not exist in this language
  "132016", // template disabled
  "contact_without_identity",
  "media_missing",
]);

/**
 * Failures someone must fix first (payment, account, number, a paused template). Once fixed, the
 * same message is sent again by hand from the bubble; never by itself, since the fix can take
 * hours and a message arriving that late is out of place.
 */
export const FIX_FIRST_SEND_ERRORS: ReadonlySet<string> = new Set([
  "131042", // payment problem on the business account (currency, payment method, credit line)
  "131031", // business account locked
  "132015", // template paused for low quality
  "133010", // business number not registered
  "credential_invalid", // the number must be connected again
]);

/** Whether a person may send a failed message again; the 24-hour window is checked apart. */
export function canRetrySend(errorCode: string | null | undefined): boolean {
  return !errorCode || !FINAL_SEND_ERRORS.has(errorCode);
}

/** Whether the queue may try again by itself: only failures that pass without anyone acting. */
export function retriesAlone(errorCode: string | null | undefined): boolean {
  return canRetrySend(errorCode) && !(errorCode && FIX_FIRST_SEND_ERRORS.has(errorCode));
}

/** Meta error codes with a written explanation in the catalogs (`delivery.error.<code>`). */
export const EXPLAINED_SEND_ERRORS: readonly string[] = [
  "131047",
  "131026",
  "131049",
  "131050",
  "131051",
  "131053",
  "131056",
  "130429",
  "131048",
  "131042",
  "131031",
  "368",
  "131000",
  "131005",
  "132000",
  "132001",
  "132015",
  "132016",
  "133010",
  "130472",
  "credential_invalid",
  "media_missing",
  "contact_without_identity",
];

/**
 * Reactions, as Meta takes them: only on a message received from the contact, up to 30 days
 * old and not deleted, while the 24-hour window is open. One emoji each; an empty one takes the
 * reaction away.
 */
export const REACTION_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

export function isReactionEmoji(value: string): boolean {
  if (value === "") return true;
  const graphemes = [...new Intl.Segmenter("en", { granularity: "grapheme" }).segment(value)];
  return (
    graphemes.length === 1 && /\p{Extended_Pictographic}|\p{Regional_Indicator}|⃣/u.test(value)
  );
}
